import { Injectable } from '@angular/core';
import { State, Selector, StateContext, Action, createSelector } from '@ngxs/store';
import { ApiService } from '../services/api.service';
import { firstValueFrom } from 'rxjs';
import produce from 'immer';
import {
  CreateProject,
  CreateProjectDesign,
  DeleteDepartment,
  DeleteProject,
  ListProjects,
  LoadDepartments,
  LoadProjectDesignLayers,
  CreateProjectDesignLayer,
  UpdateProjectDesignLayer,
  DeleteProjectDesignLayer,
  SaveProjectDesignLayerFeatures,
  ProjectsStateModel,
  RenameDepartment,
  SetUploadKeepActiveInProject,
  UpdateProject,
  UpdateProjectKeepActiveDefault,
} from './models/projects.state.model';
import { Department } from '../interfaces/department.interface';

const defaultProjectsState = {
  projects: {},
  allDepartments: [] as Department[],
  designLayersByProjectId: {} as Record<string, any[]>,
};

@State<ProjectsStateModel>({
  name: 'projects',
  defaults: defaultProjectsState,
})
@Injectable()
export class ProjectsState {
  @Selector([ProjectsState])
  static projects(state: ProjectsStateModel) {
    return Object.values(state.projects);
  }

  @Selector([ProjectsState])
  static departments(state: ProjectsStateModel): Department[] {
    return [...state.allDepartments].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  static getProjectById(projectId: string) {
    return createSelector([ProjectsState], (state: ProjectsStateModel) => {
      return state.projects[projectId];
    });
  }

  static getDesignLayers(projectId: string) {
    return createSelector([ProjectsState], (state: ProjectsStateModel) => {
      return state.designLayersByProjectId[projectId] ?? [];
    });
  }

  constructor(private apiService: ApiService) {}

  @Action(ListProjects)
  async listProjects(ctx: StateContext<ProjectsStateModel>, action: ListProjects) {
    const [projects, allDepartments] = await Promise.all([
      firstValueFrom(this.apiService.listProjectsInOrganisation({ organisationId: action.payload.organisationId })),
      firstValueFrom(this.apiService.listDepartments(action.payload.organisationId)),
    ]);
    const projectsMap = projects.reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {});
    ctx.patchState({ projects: projectsMap, allDepartments });
  }

  @Action(CreateProject)
  async createProject(ctx: StateContext<ProjectsStateModel>, action: CreateProject) {
    const project = await firstValueFrom(
      this.apiService.createProjectInOrganisation(action.payload.organisationId, {
        ...action.payload,
        department: action.payload.department_name,
      })
    );
    const state = produce(ctx.getState(), (draft) => {
      draft.projects[project.id] = project;
      if (project.department) {
        const idx = draft.allDepartments.findIndex((d) => d.id === project.department!.id);
        if (idx === -1) {
          draft.allDepartments.push(project.department);
        }
      }
    });
    ctx.setState(state);
  }

  @Action(UpdateProject)
  async updateProject(ctx: StateContext<ProjectsStateModel>, action: UpdateProject) {
    const project = await firstValueFrom(
      this.apiService.updateProjectInOrganisation(action.payload.organisationId, {
        id: action.payload.id,
        name: action.payload.name,
        description: action.payload.description,
        code: action.payload.code,
        fileIds: action.payload.fileIds,
        chief_executor_id: action.payload.chief_executor_id,
        department: action.payload.department_name,
      })
    );
    const state = produce(ctx.getState(), (draft) => {
      draft.projects[project.id] = project;
      if (project.department) {
        const idx = draft.allDepartments.findIndex((d) => d.id === project.department!.id);
        if (idx === -1) {
          draft.allDepartments.push(project.department);
        }
      }
    });
    ctx.setState(state);
  }

  @Action(UpdateProjectKeepActiveDefault)
  async updateProjectKeepActiveDefault(
    ctx: StateContext<ProjectsStateModel>,
    action: UpdateProjectKeepActiveDefault
  ) {
    const project = await firstValueFrom(
      this.apiService.updateProjectKeepActiveDefault(
        action.payload.organisationId,
        action.payload.projectId,
        action.payload.keepActiveDefault
      )
    );
    const state = produce(ctx.getState(), (draft) => {
      draft.projects[project.id] = project;
    });
    ctx.setState(state);
  }

  @Action(SetUploadKeepActiveInProject)
  setUploadKeepActiveInProject(ctx: StateContext<ProjectsStateModel>, action: SetUploadKeepActiveInProject) {
    const state = produce(ctx.getState(), (draft) => {
      const project = draft.projects[action.payload.projectId];
      const upload = project?.uploads.find((u) => u.id === action.payload.uploadId);
      if (upload) {
        upload.keepActive = action.payload.keepActive;
      }
    });
    ctx.setState(state);
  }

  @Action(DeleteProject)
  async deleteProject(ctx: StateContext<ProjectsStateModel>, action: DeleteProject) {
    await firstValueFrom(
      this.apiService.deleteProjectInOrganisation(action.payload.organisationId, action.payload.projectId)
    );
    const state = produce(ctx.getState(), (draft) => {
      delete draft.projects[action.payload.projectId];
    });
    ctx.setState(state);
  }

  @Action(CreateProjectDesign)
  async createProjectDesign(ctx: StateContext<ProjectsStateModel>, action: CreateProjectDesign) {
    const project = await firstValueFrom(
      this.apiService.createProjectDesign({
        organisationId: action.payload.organisationId,
        projectId: action.payload.projectId,
        featureCollection: action.payload.featureCollection,
      })
    );

    const state = produce(ctx.getState(), (draft) => {
      draft.projects[action.payload.projectId] = project;
    });
    ctx.setState(state);
  }

  @Action(RenameDepartment)
  async renameDepartment(ctx: StateContext<ProjectsStateModel>, action: RenameDepartment) {
    const renamed = await firstValueFrom(
      this.apiService.renameDepartment(
        action.payload.organisationId,
        action.payload.departmentId,
        action.payload.newName
      )
    );
    const state = produce(ctx.getState(), (draft) => {
      Object.values(draft.projects).forEach((p) => {
        if (p.department?.id === action.payload.departmentId) {
          p.department = renamed;
        }
      });
      const idx = draft.allDepartments.findIndex((d) => d.id === action.payload.departmentId);
      if (idx !== -1) {
        draft.allDepartments[idx] = renamed;
      }
    });
    ctx.setState(state);
  }

  @Action(DeleteDepartment)
  async deleteDepartment(ctx: StateContext<ProjectsStateModel>, action: DeleteDepartment) {
    await firstValueFrom(this.apiService.deleteDepartment(action.payload.organisationId, action.payload.departmentId));
    const state = produce(ctx.getState(), (draft) => {
      Object.values(draft.projects).forEach((p) => {
        if (p.department?.id === action.payload.departmentId) {
          p.department = null;
        }
      });
      draft.allDepartments = draft.allDepartments.filter((d) => d.id !== action.payload.departmentId);
    });
    ctx.setState(state);
  }

  @Action(LoadDepartments)
  async loadDepartments(ctx: StateContext<ProjectsStateModel>, action: LoadDepartments) {
    const allDepartments = await firstValueFrom(this.apiService.listDepartments(action.payload.organisationId));
    ctx.patchState({ allDepartments });
  }

  @Action(LoadProjectDesignLayers)
  async loadProjectDesignLayers(ctx: StateContext<ProjectsStateModel>, action: LoadProjectDesignLayers) {
    const layers = await firstValueFrom(
      this.apiService.listProjectDesignLayers(action.payload.organisationId, action.payload.projectId)
    );
    const state = produce(ctx.getState(), (draft) => {
      draft.designLayersByProjectId[action.payload.projectId] = layers;
    });
    ctx.setState(state);
  }

  @Action(CreateProjectDesignLayer)
  async createProjectDesignLayer(ctx: StateContext<ProjectsStateModel>, action: CreateProjectDesignLayer) {
    const layer = await firstValueFrom(
      this.apiService.createProjectDesignLayer(
        action.payload.organisationId,
        action.payload.projectId,
        action.payload.name,
        action.payload.color
      )
    );
    const state = produce(ctx.getState(), (draft) => {
      if (!draft.designLayersByProjectId[action.payload.projectId]) {
        draft.designLayersByProjectId[action.payload.projectId] = [];
      }
      draft.designLayersByProjectId[action.payload.projectId].push(layer);
    });
    ctx.setState(state);
  }

  @Action(UpdateProjectDesignLayer)
  async updateProjectDesignLayer(ctx: StateContext<ProjectsStateModel>, action: UpdateProjectDesignLayer) {
    const updated = await firstValueFrom(
      this.apiService.updateProjectDesignLayer(
        action.payload.organisationId,
        action.payload.projectId,
        action.payload.layerId,
        action.payload.name,
        action.payload.color
      )
    );
    const state = produce(ctx.getState(), (draft) => {
      const layers = draft.designLayersByProjectId[action.payload.projectId] ?? [];
      const idx = layers.findIndex((l) => l.id === action.payload.layerId);
      if (idx !== -1) layers[idx] = updated;
    });
    ctx.setState(state);
  }

  @Action(DeleteProjectDesignLayer)
  async deleteProjectDesignLayer(ctx: StateContext<ProjectsStateModel>, action: DeleteProjectDesignLayer) {
    const result = await firstValueFrom(
      this.apiService.deleteProjectDesignLayer(
        action.payload.organisationId,
        action.payload.projectId,
        action.payload.layerId
      )
    );
    const state = produce(ctx.getState(), (draft) => {
      const layers = draft.designLayersByProjectId[action.payload.projectId] ?? [];
      draft.designLayersByProjectId[action.payload.projectId] = layers.filter((l) => l.id !== action.payload.layerId);
      const project = draft.projects[action.payload.projectId];
      if (project) project.designUploaded = result.designUploaded;
    });
    ctx.setState(state);
  }

  @Action(SaveProjectDesignLayerFeatures)
  async saveProjectDesignLayerFeatures(_ctx: StateContext<ProjectsStateModel>, action: SaveProjectDesignLayerFeatures) {
    await firstValueFrom(
      this.apiService.saveProjectDesignLayerFeatures(
        action.payload.organisationId,
        action.payload.projectId,
        action.payload.layerId,
        action.payload.featureCollection
      )
    );
  }
}
